import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Download, Users, FileText, BookOpen, ChevronDown, Loader2, Wallet } from 'lucide-react';
import { User, Match, Bet, UserTier, BetStatus, MatchStatus, toMYT } from '@/lib/index';
import { FightSetting } from '@/hooks/useUserManagement';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

interface BackupButtonProps {
  currentUser: User;
  allUsers: User[];
  allBets: Bet[];
  allMatches: Match[];
  fightSettings: Map<string, FightSetting>;
}

// ── CSV helpers ───────────────────────────────────────────────────────────────

function csvEscape(val: string | number | boolean | null | undefined): string {
  if (val === null || val === undefined) return '';
  const str = String(val);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function buildCsv(headers: string[], rows: (string | number | boolean | null | undefined)[][]): string {
  const lines: string[] = [headers.map(csvEscape).join(',')];
  for (const row of rows) {
    lines.push(row.map(csvEscape).join(','));
  }
  return lines.join('\r\n');
}

function downloadCsv(filename: string, content: string) {
  const blob = new Blob(['\uFEFF' + content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function fmtDate(ts: string | null | undefined): string {
  if (!ts) return '';
  try { return format(toMYT(ts), 'yyyy-MM-dd HH:mm:ss') + ' MYT'; } catch { return ts; }
}

function getDescendantIds(userId: string, allUsers: User[]): string[] {
  const result: string[] = [];
  const recurse = (id: string) => {
    allUsers.filter(u => u.parentId === id).forEach(c => { result.push(c.id); recurse(c.id); });
  };
  recurse(userId);
  return result;
}

/** Get the effective fight% a parent has against a child (0 if off or no setting) */
function getEffectiveFightPct(
  parentId: string,
  childId: string,
  fightSettings: Map<string, FightSetting>,
): number {
  const key = `${parentId}:${childId}`;
  const fs = fightSettings.get(key);
  if (!fs || !fs.fightOnOff) return 0;
  return fs.fightPct ?? 0;
}

/** Build a label like "5%" or "Off" for a fight setting */
function fightPctLabel(parentId: string, childId: string, fightSettings: Map<string, FightSetting>): string {
  const key = `${parentId}:${childId}`;
  const fs = fightSettings.get(key);
  if (!fs) return 'Off';
  if (!fs.fightOnOff) return 'Off';
  return `${fs.fightPct ?? 0}%`;
}

// ── Export: User System ───────────────────────────────────────────────────────

function exportUserSystem(
  currentUser: User,
  allUsers: User[],
  fightSettings: Map<string, FightSetting>,
) {
  const visibleIds = new Set([currentUser.id, ...getDescendantIds(currentUser.id, allUsers)]);
  const visible = allUsers.filter(u => visibleIds.has(u.id));

  // Build parent map
  const parentMap: Record<string, string> = {};
  for (const u of allUsers) parentMap[u.id] = u.username;

  // Collect all unique parent→child fight relationships among visible users
  // We'll add one column per direct child the user has fight settings against
  // Instead, we add fight config columns inline per row showing parent's settings vs this user

  const headers = [
    'Username', 'Nickname', 'Description', 'Tier', 'Status',
    'Rebate (%)', 'Fight % Limit', 'Credit Limit', 'Available Credit',
    'Credit Refresh Rate', 'Parent Username',
    // Fight config columns
    'Blanket Fight On/Off', 'Blanket Fight %',
    'Fight On/Off (vs Parent)', 'Fight % (vs Parent)',
    // Per-child fight settings summary (for agents/company who have children)
    'Fight Settings vs Downlines',
    'Created At',
  ];

  const rows = visible.map(u => {
    // Blanket sentinel: fight_settings row where parent_id = child_id = u.id
    const blanketKey = `${u.id}:${u.id}`;
    const blanketSetting = fightSettings.get(blanketKey);
    const blanketOnOff = blanketSetting ? (blanketSetting.fightOnOff ? 'On' : 'Off') : 'Off';
    const blanketPct   = blanketSetting && blanketSetting.fightOnOff ? `${blanketSetting.fightPct ?? 0}%` : '0%';

    // How parent sees this user (parent's fight against this user)
    const parentFightLabel = u.parentId
      ? fightPctLabel(u.parentId, u.id, fightSettings)
      : 'N/A';
    const parentFightOnOff = u.parentId
      ? (() => { const k = `${u.parentId}:${u.id}`; const fs = fightSettings.get(k); return fs ? (fs.fightOnOff ? 'On' : 'Off') : 'Off'; })()
      : 'N/A';

    // Build fight settings vs each direct child
    const directChildren = allUsers.filter(c => c.parentId === u.id && visibleIds.has(c.id));
    const childFightSummary = directChildren.length === 0
      ? ''
      : directChildren.map(c => {
          const lbl = fightPctLabel(u.id, c.id, fightSettings);
          const key2 = `${u.id}:${u.id}`; // blanket
          const _ = fightSettings.get(key2); void _;
          return `${c.username}:${lbl}`;
        }).join(' | ');

    return [
      u.username,
      u.nickname ?? '',
      u.description ?? '',
      u.tier,
      u.status,
      ((1 - u.ticketPrice) * 100).toFixed(2),
      u.fightPctLimit,
      u.creditLimit,
      u.availableCreditLimit ?? u.creditLimit,
      u.creditRefreshRate ?? 'manual',
      u.parentId ? (parentMap[u.parentId] ?? u.parentId) : '',
      // Fight config
      blanketOnOff,
      blanketPct,
      parentFightOnOff,
      parentFightLabel,
      childFightSummary,
      fmtDate(u.createdAt),
    ];
  });

  const ts = format(new Date(), 'yyyyMMdd_HHmmss');
  downloadCsv(`UserSystem_${ts}.csv`, buildCsv(headers, rows));
}

// ── Export: Bet History ───────────────────────────────────────────────────────

function exportBetHistory(
  currentUser: User,
  allUsers: User[],
  allBets: Bet[],
  allMatches: Match[],
  fightSettings: Map<string, FightSetting>,
) {
  const visibleIds = new Set([currentUser.id, ...getDescendantIds(currentUser.id, allUsers)]);
  const matchMap: Record<string, Match> = {};
  for (const m of allMatches) matchMap[m.id] = m;
  const userMap: Record<string, User> = {};
  for (const u of allUsers) userMap[u.id] = u;
  if (currentUser) userMap[currentUser.id] = currentUser;

  const relevantBets = allBets.filter(b => visibleIds.has(b.userId));

  // Each bet = one row, one score per row
  const headers = [
    'Transaction ID', 'Username', 'Tier', 'Parent Username',
    'Match', 'Match Date (MYT)', 'Score Selection', 'Odds',
    'Bet Amount', 'Rebate (%)', 'Ticket Price',
    'Fight % (Snapshot)',        // snap: agent's fight vs player at bet time
    'Fight % Chain (Snapshot)',  // full ancestry chain fight% at bet time
    'Fight % (vs Parent, Live)', // live: viewer's current fight% against this user's parent
    'Fight % vs Downlines (Live)', // live: this user's current fight% settings vs their children
    'Status', 'Placed At', 'Updated At', 'Remark',
  ];

  const rows = relevantBets.map(b => {
    const bu = userMap[b.userId];
    const m = matchMap[b.matchId];
    const matchName = m ? `${m.homeTeam} vs ${m.awayTeam}` : b.matchId;
    const matchDate = m ? fmtDate(m.startTime) : '';
    const username  = bu?.username ?? b.userId;
    const tier      = bu?.tier ?? '';
    const parentUser = bu?.parentId ? userMap[bu.parentId] : null;
    const parentName = parentUser?.username ?? '';

    // Snapshot-based fight% (historically correct)
    const snapFight = b.fightPctSnapshot ?? b.fightPercentage ?? 0;
    const chainFight = (b as any).fightPctSnapshotChain ?? '';

    // Live fight% vs parent (viewer's current setting against this user)
    const liveVsParentPct = bu?.parentId
      ? getEffectiveFightPct(bu.parentId, b.userId, fightSettings)
      : 0;
    const liveVsParentLabel = bu?.parentId
      ? fightPctLabel(bu.parentId, b.userId, fightSettings)
      : 'N/A';

    // Live fight% this user (bettor) has vs their own direct children
    const betterChildren = allUsers.filter(c => c.parentId === b.userId && visibleIds.has(c.id));
    const childFightLive = betterChildren.length === 0
      ? ''
      : betterChildren.map(c => `${c.username}:${fightPctLabel(b.userId, c.id, fightSettings)}`).join(' | ');

    // Rebate
    const tp = bu?.ticketPrice ?? b.ticketPrice ?? 1;
    const rebatePct = ((1 - tp) * 100).toFixed(2);

    return [
      b.transactionId ?? b.id,
      username,
      tier,
      parentName,
      matchName,
      matchDate,
      b.selection,
      b.oddsAtTime,
      b.amount,
      `${rebatePct}%`,
      tp,
      snapFight !== 0 ? `${snapFight}%` : '0%',
      chainFight !== '' ? `${chainFight}%` : '0%',
      liveVsParentLabel,
      childFightLive,
      b.status,
      fmtDate(b.createdAt),
      fmtDate(b.updatedAt),
      b.remark ?? '',
    ];
  });

  // Sort by placed time descending
  rows.sort((a, b) => String(b[16] ?? '').localeCompare(String(a[16] ?? '')));

  const ts = format(new Date(), 'yyyyMMdd_HHmmss');
  downloadCsv(`BetHistory_${ts}.csv`, buildCsv(headers, rows));
}

// ── Export: Report ────────────────────────────────────────────────────────────

function exportReport(
  currentUser: User,
  allUsers: User[],
  allBets: Bet[],
  allMatches: Match[],
  fightSettings: Map<string, FightSetting>,
) {
  const visibleIds = new Set([currentUser.id, ...getDescendantIds(currentUser.id, allUsers)]);
  const matchMap: Record<string, Match> = {};
  for (const m of allMatches) matchMap[m.id] = m;
  const userMap: Record<string, User> = {};
  for (const u of allUsers) userMap[u.id] = u;
  if (currentUser) userMap[currentUser.id] = currentUser;

  // Each SCORE SELECTION = one row (not grouped by transaction)
  // All bets in the system, visible to this company user
  const relevantBets = allBets.filter(b => visibleIds.has(b.userId) && b.status !== BetStatus.CANCELLED);

  const headers = [
    'Transaction ID', 'Username', 'Tier', 'Parent Username',
    'Match', 'Match Date (MYT)',
    'Score Selection', 'Odds',
    'Bet Amount', 'Rebate (%)', 'Rebate Amount', 'Net After Rebate',
    'Fight % (Snapshot)',         // agent→player fight at bet time
    'Fight % Chain (Snapshot)',   // full ancestry chain fight% at bet time
    'Fight % (vs Parent, Live)',  // viewer's live fight% vs this bettor's parent
    'Fight % vs Downlines (Live)', // this bettor's live fight% vs their children
    'Win/Lose Amount',
    'Status', 'Placed At',
  ];

  const rows: (string | number | null | undefined)[][] = [];

  for (const b of relevantBets) {
    const bu = userMap[b.userId];
    const m = matchMap[b.matchId];
    const matchName = m ? `${m.homeTeam} vs ${m.awayTeam}` : b.matchId;
    const matchDate = m ? fmtDate(m.startTime) : '';
    const username  = bu?.username ?? b.userId;
    const tier      = bu?.tier ?? '';
    const parentUser = bu?.parentId ? userMap[bu.parentId] : null;
    const parentName = parentUser?.username ?? '';

    // Rebate
    const tp = bu?.ticketPrice ?? b.ticketPrice ?? 1;
    const rebateRate = 1 - tp;
    const rebateAmt  = b.amount * rebateRate;
    const netAfterRebate = b.amount - rebateAmt;

    // Fight % columns
    const snapFight  = b.fightPctSnapshot ?? b.fightPercentage ?? 0;
    const chainFight = (b as any).fightPctSnapshotChain ?? '';

    // Live fight% this user's parent has against them
    const liveVsParentLabel = bu?.parentId
      ? fightPctLabel(bu.parentId, b.userId, fightSettings)
      : 'N/A';

    // Live fight% this user has vs their own direct children
    const bettorChildren = allUsers.filter(c => c.parentId === b.userId && visibleIds.has(c.id));
    const childFightLive = bettorChildren.length === 0
      ? ''
      : bettorChildren.map(c => `${c.username}:${fightPctLabel(b.userId, c.id, fightSettings)}`).join(' | ');

    // Win/Lose
    const isWon  = b.status === BetStatus.WON;
    const isLost = b.status === BetStatus.LOST;
    const wlAmt  = isWon ? b.amount * b.oddsAtTime : isLost ? -b.amount : 0;

    rows.push([
      b.transactionId ?? b.id,
      username,
      tier,
      parentName,
      matchName,
      matchDate,
      b.selection,
      b.oddsAtTime,
      b.amount,
      `${(rebateRate * 100).toFixed(2)}%`,
      rebateAmt.toFixed(2),
      netAfterRebate.toFixed(2),
      snapFight !== 0 ? `${snapFight}%` : '0%',
      chainFight !== '' ? `${chainFight}%` : '0%',
      liveVsParentLabel,
      childFightLive,
      wlAmt.toFixed(2),
      b.status,
      fmtDate(b.createdAt),
    ]);
  }

  // Sort by placed time descending
  rows.sort((a, b) => String(b[18] ?? '').localeCompare(String(a[18] ?? '')));

  const ts = format(new Date(), 'yyyyMMdd_HHmmss');
  downloadCsv(`Report_${ts}.csv`, buildCsv(headers, rows));
}

// ── Export: Wallet Balance ────────────────────────────────────────────────────
// Mirrors the Book6 settlement formulas from WalletBalance.tsx
// Each individual settled bet = one row, showing all 13 BookRow columns.

type WBBookRow = {
  origBP: number;
  bpT: number; commT: number; wlT: number; totalT: number;
  bpF: number; commF: number; wlF: number; totalF: number;
  bpC: number; commC: number; wlC: number; totalC: number;
};

function wbLeafRow(
  bp: number, wl: number,
  parentTP: number, // decimal (e.g. 0.9)
  fightPct: number  // decimal (e.g. 0.05)
): WBBookRow {
  const origBP = bp;
  const bpT    = bp;
  const commT  = bpT * (1 - parentTP);           // F = E*(1-parentTP)
  const wlT    = wl;                              // G = wl
  const totalT = (-bpT) + commT + wlT;           // H = (-1*E)+F+G  per spec
  const bpF    = bpT * fightPct;                 // J = E*fightPct
  // K = (-J*parentRebate)+(E*parentRebate)-F  per spec
  const parentRebate = 1 - parentTP;
  const commF  = (-bpF * parentRebate) + (bpT * parentRebate) - commT;
  const wlF    = wlT * fightPct * -1;            // L = G*fightPct*-1
  const totalF = bpF + commF + wlF;              // M = J+K+L
  const bpC    = origBP - bpF;                   // N = D-J
  const commC  = -bpC * (1 - parentTP);          // O = -N*(1-parentTP)
  const wlC    = -(wlT + wlF);                   // P = -(G+L)
  const totalC = bpC + commC + wlC;              // Q = N+O+P
  return { origBP, bpT, commT, wlT, totalT, bpF, commF, wlF, totalF, bpC, commC, wlC, totalC };
}

function exportWalletBalance(
  currentUser: User,
  allUsers: User[],
  allBets: Bet[],
  allMatches: Match[],
  fightSettings: Map<string, FightSetting>,
) {
  const visibleIds = new Set([currentUser.id, ...getDescendantIds(currentUser.id, allUsers)]);
  const matchMap: Record<string, Match> = {};
  for (const m of allMatches) matchMap[m.id] = m;
  const userMap: Record<string, User> = {};
  for (const u of allUsers) userMap[u.id] = u;
  userMap[currentUser.id] = currentUser;

  // Only settled (WON/LOST) bets from completed matches
  const relevantBets = allBets.filter(b => {
    if (!visibleIds.has(b.userId)) return false;
    const m = matchMap[b.matchId];
    if (!m || m.status !== MatchStatus.COMPLETED) return false;
    return b.status === BetStatus.WON || b.status === BetStatus.LOST;
  });

  const headers = [
    'Transaction ID', 'Username', 'Nickname', 'Tier', 'Parent Username',
    'Match', 'Match Date (MYT)', 'Score Selection', 'Odds',
    'Fight % (Snapshot)',
    // Downline group
    'DL: Orig Bet', 'DL: Bet Placed', 'DL: Commission', 'DL: Win/Lose', 'DL: Total',
    // Fight group
    'FT: Bet Placed', 'FT: Commission', 'FT: Win/Lose', 'FT: Total',
    // Company group
    'CM: Bet Placed', 'CM: Commission', 'CM: Win/Lose', 'CM: Total',
    'Status', 'Placed At (MYT)',
  ];

  const rows: (string | number | null | undefined)[][] = [];

  for (const b of relevantBets) {
    const bu = userMap[b.userId];
    const m = matchMap[b.matchId];
    const matchName = m ? `${m.homeTeam} vs ${m.awayTeam}` : b.matchId;
    const matchDate = m ? fmtDate(m.startTime) : '';
    const username  = bu?.username ?? b.userId;
    const nickname  = bu?.nickname ?? '';
    const tier      = bu?.tier ?? '';
    const parentUser = bu?.parentId ? userMap[bu.parentId] : null;
    const parentName = parentUser?.username ?? '';

    // Fight% at bet time (snapshot)
    const snapFight = b.fightPctSnapshot ?? (b as any).fightPercentage ?? 0;
    const fightPct  = snapFight / 100; // convert to decimal

    // Parent's ticket price (decimal) — used for commissions
    const parentTP = parentUser
      ? (parentUser.ticketPrice ?? 1)
      : 1;

    // Win/lose
    const isWon = b.status === BetStatus.WON;
    const wl    = isWon ? b.amount * b.oddsAtTime : 0;

    // Compute settlement using leafRow formula
    const r = wbLeafRow(b.amount, wl, parentTP, fightPct);

    rows.push([
      b.transactionId ?? b.id,
      username,
      nickname,
      tier,
      parentName,
      matchName,
      matchDate,
      b.selection,
      b.oddsAtTime,
      snapFight !== 0 ? `${snapFight}%` : '0%',
      // Downline group
      Math.max(0, r.origBP).toFixed(2),
      Math.max(0, r.bpT).toFixed(2),
      r.commT.toFixed(2),
      r.wlT.toFixed(2),
      r.totalT.toFixed(2),
      // Fight group
      Math.max(0, r.bpF).toFixed(2),
      r.commF.toFixed(2),
      r.wlF.toFixed(2),
      r.totalF.toFixed(2),
      // Company group
      Math.max(0, r.bpC).toFixed(2),
      r.commC.toFixed(2),
      r.wlC.toFixed(2),
      r.totalC.toFixed(2),
      b.status,
      fmtDate(b.createdAt),
    ]);
  }

  // Sort by placed time descending
  rows.sort((a, b) => String(b[b.length - 1] ?? '').localeCompare(String(a[a.length - 1] ?? '')));

  const ts = format(new Date(), 'yyyyMMdd_HHmmss');
  downloadCsv(`WalletBalance_${ts}.csv`, buildCsv(headers, rows));
}

// ── Component ─────────────────────────────────────────────────────────────────

export function BackupButton({ currentUser, allUsers, allBets, allMatches, fightSettings }: BackupButtonProps) {
  const [open, setOpen] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handle = useCallback(async (type: 'users' | 'bets' | 'report' | 'wallet') => {
    setDownloading(type);
    setOpen(false);
    await new Promise(r => setTimeout(r, 60));
    try {
      if (type === 'users')  exportUserSystem(currentUser, allUsers, fightSettings);
      if (type === 'bets')   exportBetHistory(currentUser, allUsers, allBets, allMatches, fightSettings);
      if (type === 'report') exportReport(currentUser, allUsers, allBets, allMatches, fightSettings);
      if (type === 'wallet') exportWalletBalance(currentUser, allUsers, allBets, allMatches, fightSettings);
    } finally {
      setDownloading(null);
    }
  }, [currentUser, allUsers, allBets, allMatches, fightSettings]);

  const items = [
    { key: 'users'  as const, icon: Users,    label: 'User System' },
    { key: 'bets'   as const, icon: BookOpen,  label: 'Bet History' },
    { key: 'report' as const, icon: FileText,  label: 'Report' },
    { key: 'wallet' as const, icon: Wallet,    label: 'Wallet Balance' },
  ];

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(v => !v)}
        className={cn(
          'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border',
          'bg-primary/10 hover:bg-primary/20 text-primary border-primary/30 hover:border-primary/50',
          downloading && 'opacity-70 cursor-not-allowed'
        )}
        disabled={!!downloading}
        title="Backup / Download data"
      >
        {downloading ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <Download className="w-3.5 h-3.5" />
        )}
        <span>Backup</span>
        <ChevronDown className={cn('w-3 h-3 transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-1 z-[200] bg-popover border border-border rounded-xl shadow-xl py-1 min-w-[160px]">
          {items.map(({ key, icon: Icon, label }) => (
            <button
              key={key}
              onClick={() => handle(key)}
              className="w-full flex items-center gap-2.5 px-4 py-2.5 text-xs font-medium hover:bg-accent hover:text-accent-foreground transition-colors text-left"
            >
              <Icon className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
